/**
 * `target_filter` → the scope editor's four-part state, for both relationship sheets.
 *
 * `deriveApplicability` reads a rule made only of `label` / `type` filters as a TYPE LIST and
 * anything else as a condition, so the two readings never overlap in what is STORED. They do
 * overlap in the EDITOR: switching the mode strip from Custom back to a type list keeps the
 * conditions in state while `buildApplicability` writes only the list — which is the state the
 * scope editor's *Carried over from the old definition* note exists to report.
 *
 * `modeOverride` declares that mode. Given `specific`, a rule that reads back as custom is
 * re-read the way the editor holds it: its `label` / `type` conditions ARE the list, and
 * everything else is the leftover the note names.
 */
import type { FilterExpression, UnifiedFilters } from 'types/filters';
import { deriveApplicability } from '../../../utils/contextFlowSerialization';
import type { ApplicabilityMode } from '../ApplicabilityEditor/ApplicabilityEditorMolecule';
import type { FilterState } from '../../../types/filterState';

const EMPTY_FILTER_STATE: FilterState = {
  propertyFilters: [],
  perspectiveFilters: [],
  relationshipFilters: [],
  arcFilters: [],
  filterGroups: [],
};

export interface RelationshipScopeState {
  mode: ApplicabilityMode;
  families: string[];
  types: string[];
  filter: FilterState;
}

/** The values a `label` / `type` membership condition names, however it stored them. */
const readValues = (f: FilterExpression | undefined): string[] => {
  if (!f) return [];
  if (Array.isArray(f.values)) return f.values.filter((v): v is string => typeof v === 'string');
  if (f.value != null) return [String(f.value)];
  return [];
};

export function readRelationshipScope(
  targetFilter: UnifiedFilters | undefined,
  modeOverride?: ApplicabilityMode
): RelationshipScopeState {
  const derived = deriveApplicability(targetFilter);
  const mode = modeOverride ?? ((derived.applicabilityMode ?? 'any') as ApplicabilityMode);
  const filter = derived.applicabilityFilter ?? EMPTY_FILTER_STATE;

  if (mode === 'specific' && derived.applicabilityMode === 'custom') {
    const pf = filter.propertyFilters ?? [];
    return {
      mode,
      families: readValues(pf.find((f) => f.property === 'label')),
      types: readValues(pf.find((f) => f.property === 'type')),
      filter: { ...filter, propertyFilters: pf.filter((f) => f.property !== 'label' && f.property !== 'type') },
    };
  }

  return {
    mode,
    families: derived.applicabilityFamilies ?? [],
    types: derived.applicabilityTypes ?? [],
    filter,
  };
}
